import React from 'react'

export const Strings = {
  /**
   * Copies the specified text to the clipboard using the Clipboard API.
   *
   * @async
   * @param {string} text - The text to copy to the clipboard.
   * @returns {Promise<void>} A promise that resolves once the text has been copied.
   *
   * @example
   * // Copies text to the clipboard
   * Strings.copyToClipboard('Hello World');
   */
  async copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text)
    } catch (err) {
      console.error('Error in copyText: ', err)
    }
  },
}

const version = 2

// Utility function to set a cookie
export function setCookie(name, value, days) {
  const d = new Date();
  d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = "expires=" + d.toUTCString();
  document.cookie = name + "=" + encodeURIComponent(value) + ";" + expires + ";path=/;domain=.chaindlegame.com";
}

// Utility function to get a cookie value
export function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return decodeURIComponent(parts.pop().split(';').shift());
  return null;
}

// Utility function to delete a cookie
export function deleteCookie(name) {
  document.cookie = name + "=; Max-Age=0; path=/; domain=.chaindlegame.com";
}

// Helper function to detect if the domain is dev or www
export function isDevDomain() {
  return window.location.hostname.startsWith("dev");
}

export const Storage = {
  isDevDomain,
  initializeNewGameData() {
    const newData = {
      version,
      standard4: {
        currentGame: {},
      },
    };
    return newData;
  },

  initializeNewStats() {
    const newData = {
      version,
      standard4: {
        losses: 0,
        streak: 0,
        longestStreak: 0,
        gamesPlayed: 0,
        topScores: 0,
        worstChain: 0,
        avgDistanceFromAvg: 0,
        avgDistanceFromBest: 0,
      },
    };
    return newData;
  },

  getFromLocalStorage(key, defaultValue = null) {
    try {
      // For www, if local storage is empty, check the cookie
      if (!this.isDevDomain()) {
        const data = localStorage.getItem(key);
        if (data) {
          return JSON.parse(data);
        }

        // Fallback to checking the cookie if local storage is empty
        const cookieData = getCookie(key);
        if (cookieData) {
          const parsedData = JSON.parse(cookieData);

          // Write the cookie data to local storage and delete the cookie
          localStorage.setItem(key, JSON.stringify(parsedData));
          deleteCookie(key);

          return parsedData;
        }

        return defaultValue;
      }

      // For dev, always read from local storage
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : defaultValue;
    } catch (e) {
      console.error('Failed to retrieve or parse data from local storage:', e);
      return defaultValue;
    }
  },

  saveToLocalStorage(key, value) {
    try {
      // Always write to local storage for both dev and www
      localStorage.setItem(key, JSON.stringify(value));

      // For dev domain, also write to the cookie
      if (this.isDevDomain()) {
        setCookie(key, JSON.stringify(value), 7); // 7 days cookie expiration
      }
    } catch (e) {
      console.error('Failed to save data to local storage and cookie:', e);
    }
  },

  getBoolFromLocalStorage(bool) {
    const storedValue = this.getFromLocalStorage(bool);
    return storedValue === true || storedValue === 'true';
  },

  saveBoolToLocalStorage(name, bool) {
    this.saveToLocalStorage(name, bool);
  },

  getGameData(gameMode) {
    const savedData = this.getFromLocalStorage('gameData');
    if (savedData && savedData.version === version) {
      return savedData[gameMode];
    } else if (savedData?.version === 1 && savedData[gameMode]?.currentGame) {
      return this.migrateData(
        savedData[gameMode].currentGame,
        savedData[gameMode].gameStats,
      );
    } else {
      return this.initializeNewGameData();
    }
  },

  getGameStats(gameMode) {
    const savedStats = this.getFromLocalStorage('gameStats');
    if (savedStats && savedStats.version === version) {
      return savedStats[gameMode];
    } else if (savedStats?.version === 1 && savedStats[gameMode]) {
      return this.transformStats(
        savedStats[gameMode].gameStats ?? savedStats[gameMode],
      );
    } else {
      return this.initializeNewStats(gameMode);
    }
  },

  saveCurrentGame(gameMode, currentGame) {
    let gameData = this.getFromLocalStorage('gameData');

    // Check if gameData is null or undefined and initialize it if needed
    if (!gameData) {
      gameData = { version: version };
    }

    // Ensure that the game mode exists in gameData
    if (!gameData[gameMode]) {
      gameData[gameMode] = {};
    }

    gameData[gameMode].currentGame = currentGame;
    this.saveToLocalStorage('gameData', gameData);
  },

  saveGameStats(gameMode, gameStats) {
    const savedStats = this.getFromLocalStorage('gameStats') || {}; // Fallback to an empty object if null
    savedStats[gameMode] = gameStats;
    savedStats.version = version;
    this.saveToLocalStorage('gameStats', savedStats);
  },

  migrateData(oldCurrentGame, oldStats) {
    const newState = {
      version: version,
      standard4: {
        currentGame: this.transformGameData(oldCurrentGame),
        hasSeenRules: true, // added as per the target data structure
      },
    };
    const newStats = {
      version: version,
      standard4: this.transformStats(oldStats),
    };
    this.saveToLocalStorage('gameData', newState);
    this.saveToLocalStorage('gameStats', newStats);
    return newState.standard4;
  },

  transformStats(oldStats) {
    return {
      losses: oldStats.losses,
      streak: oldStats.streak,
      longestStreak: oldStats.longestStreak,
      gamesPlayed: oldStats.gamesPlayed,
      topScores: 0,
      worstChain: 0,
      avgDistanceFromBest: oldStats.gameDistanceBest,
      avgDistanceFromAvg: oldStats.gameDistanceAvg,
    };
  },

  transformGameData(oldCurrentGame) {
    const emptyStringIndex = oldCurrentGame.gameState.findIndex(
      (wordData) => wordData[0] === ''
    );
    const winningRow = oldCurrentGame.winningRow;

    const index = emptyStringIndex === -1 ? winningRow : emptyStringIndex;
    let upper = oldCurrentGame.gameState.slice(0, index);
    let lower = oldCurrentGame.gameState.slice(index + 1).reverse();

    upper[0][1] = [0, 1, 2, 3];
    lower[0][1] = [0, 1, 2, 3];

    // Convert all number scores to arrays
    upper = upper.map((wordData) => [
      wordData[0],
      Array.isArray(wordData[1]) ? wordData[1] : [wordData[1]],
    ]);
    lower = lower.map((wordData) => [
      wordData[0],
      Array.isArray(wordData[1]) ? wordData[1] : [wordData[1]],
    ]);

    const link = oldCurrentGame.gameState[oldCurrentGame.currentGuessRow][0];

    const transformedCurrentGame = {
      upper,
      link,
      lower,
      won: oldCurrentGame.won,
      wasStarted: oldCurrentGame.wasStarted,
      bestScore: oldCurrentGame.bestScore,
      avgScore: oldCurrentGame.avgScore,
      puzzle_number: oldCurrentGame.puzzle_number,
    };

    return transformedCurrentGame;
  },
};


export const Nums = {
  /**
   * Converts a number to a string representation with a maximum of 2 decimal places.
   * If the number has fewer decimal places, it will not add unnecessary trailing zeroes.
   *
   * @param {number} num - The number to be formatted.
   * @returns {string} The formatted number as a string.
   *
   * @example
   * // returns '1.2'
   * Nums.customToFixed(1.20);
   * @example
   * // returns '1'
   * Nums.customToFixed(1);
   */
  customToFixed(num) {
    // Convert the number to a string with at most 2 decimal places
    const str = num.toFixed(2)

    // If the number was already at most 2 decimal places, return the original number
    if (
      Math.floor(num) !== num &&
      (num * 10) % 1 !== 0 &&
      (num * 100) % 1 === 0
    ) {
      return str
    }

    // Otherwise, return the number as a string without unnecessary trailing zeroes
    return parseFloat(str).toString()
  },
}

export const Words = {
  validLength(word, wordLength) {
    if (word.length !== wordLength) {
      return { result: 'error', errorMsg: 'Incorrect word length!' }
    } else return true
  },
  validWord(word, validWords) {
    if (!validWords.includes(word)) {
      return {
        result: 'error',
        errorMsg: `"${word.toUpperCase()}" is not a real word!`,
      }
    } else return true
  },
  checkWord(upper, link, lower) {
    // Check if the word is a valid match for either chain
    const upperCheck = isValidMatch(upper, link)
    const lowerCheck = isValidMatch(lower, link)

    // Check if the game is over (i.e., the word connects to both chains)
    if (upperCheck.isSuccessful && lowerCheck.isSuccessful) {
      return { result: 'LINK' }
    } else if (upperCheck.isSuccessful) {
      return { result: 'UPPER', indexOfChange: [upperCheck.indexOfChange] }
    } else if (lowerCheck.isSuccessful) {
      return { result: 'LOWER', indexOfChange: [lowerCheck.indexOfChange] }
    } else {
      const errorMsg = (
        <React.Fragment>
          {capitalizeFirstLetter(link)} doesn't follow the game's rules!
          <br />
          {capitalizeFirstLetter(upperCheck.errorMsg)} from {upper},
          <br />
          and {lowerCheck.errorMsg} from {lower}.
        </React.Fragment>
      )
      return { result: 'error', errorMsg }
    }
  },
}

/**
 * Checks if two words have exactly one letter difference and
 * returns the index of the differing letter.
 * @param {string} word1 - The first word.
 * @param {string} word2 - The second word.
 * @return {object} - An object with a success flag and the index of the differing letter.
 */
function isValidMatch(word1, word2) {
  let changed = 0
  let indexOfChange = null

  // Go through letter by letter
  for (let i = 0; i < word1.length; i++) {
    // If the letters aren't the same, count it as a change
    if (word1[i] != word2[i]) {
      changed += 1
      // Track the index of the last change
      indexOfChange = i
    }
  }
  if (changed === 0) {
    // No letters were changed
    return { isSuccessful: false, errorMsg: 'no letters changed' }
  } else if (changed > 1) {
    // Too many letters were changed
    return { isSuccessful: false, errorMsg: 'too many letters changed' }
  } else {
    // Only one letter was changed
    return { isSuccessful: true, indexOfChange: indexOfChange }
  }
}

/**
 * Capitalizes the first letter of a string.
 * @param {string} string - The string to capitalize.
 * @returns {string} - The capitalized string.
 */
function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}
